import { __rest, __assign } from 'tslib';
import React, { forwardRef, useCallback, Fragment, isValidElement, cloneElement } from 'react';
import cn from 'classnames';
import { Input } from '@alfalab/core-components-input/esm';
import { Slider } from '@alfalab/core-components-slider/esm';

var styles = {"component":"slider-input__component_pusnq","block":"slider-input__block_pusnq","slider":"slider-input__slider_pusnq","size-72":"slider-input__size-72_pusnq","hidePips":"slider-input__hidePips_pusnq","field":"slider-input__field_pusnq","size-48":"slider-input__size-48_pusnq","size-56":"slider-input__size-56_pusnq","size-64":"slider-input__size-64_pusnq","input":"slider-input__input_pusnq","steps":"slider-input__steps_pusnq","info":"slider-input__info_pusnq","hasLabel":"slider-input__hasLabel_pusnq","focused":"slider-input__focused_pusnq","filled":"slider-input__filled_pusnq"};
require('./index.css')

var SIZE_TO_CLASSNAME_MAP = {
    s: 'size-48',
    m: 'size-56',
    l: 'size-64',
    xl: 'size-72',
    48: 'size-48',
    56: 'size-56',
    64: 'size-64',
    72: 'size-72',
};
var SliderInput = forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var className = _a.className, inputClassName = _a.inputClassName, sliderClassName = _a.sliderClassName, stepsClassName = _a.stepsClassName, focusedClassName = _a.focusedClassName, fieldClassName = _a.fieldClassName, _e = _a.value, value = _e === void 0 ? '' : _e, _f = _a.min, min = _f === void 0 ? 0 : _f, _g = _a.max, max = _g === void 0 ? 100 : _g, _h = _a.step, step = _h === void 0 ? 1 : _h, _j = _a.lockLimit, lockLimit = _j === void 0 ? false : _j, block = _a.block, _k = _a.steps, steps = _k === void 0 ? [] : _k, _l = _a.sliderValue, sliderValue = _l === void 0 ? +value : _l, _m = _a.size, size = _m === void 0 ? 48 : _m, label = _a.label, info = _a.info, disabled = _a.disabled, readOnly = _a.readOnly, onChange = _a.onChange, onInputChange = _a.onInputChange, onSliderChange = _a.onSliderChange, onSliderStart = _a.onSliderStart, onSliderEnd = _a.onSliderEnd, rightAddons = _a.rightAddons, _o = _a.Input, Input$1 = _o === void 0 ? Input : _o, _p = _a.customInputProps, customInputProps = _p === void 0 ? {} : _p, error = _a.error, hint = _a.hint, pips = _a.pips, range = _a.range, dataTestId = _a.dataTestId, restProps = __rest(_a, ["className", "inputClassName", "sliderClassName", "stepsClassName", "focusedClassName", "fieldClassName", "value", "min", "max", "step", "lockLimit", "block", "steps", "sliderValue", "size", "label", "info", "disabled", "readOnly", "onChange", "onInputChange", "onSliderChange", "onSliderStart", "onSliderEnd", "rightAddons", "Input", "customInputProps", "error", "hint", "pips", "range", "dataTestId"]);
    var getValidInputValue = useCallback(function (inputValue) {
        var number = parseInt(inputValue === null || inputValue === void 0 ? void 0 : inputValue.toString().replace(/\s/g, ''), 10);
        return inputValue === '' || Number.isNaN(number) ? '' : Math.abs(number);
    }, []);
    var handleSliderChange = useCallback(function (payload) {
        if (onChange)
            onChange(null, payload);
        if (onSliderChange)
            onSliderChange(payload);
    }, [onChange, onSliderChange]);
    var handleInputChange = useCallback(function (event, payload) {
        if (onChange)
            onChange(event, { value: getValidInputValue(payload.value) });
        if (onInputChange)
            onInputChange(event, { value: getValidInputValue(payload.value) });
    }, [getValidInputValue, onChange, onInputChange]);
    var handleInputBlur = useCallback(function (event) {
        var inputValue = event.target.value;
        var validValue = getValidInputValue(inputValue);
        var getEventPayloadValue = function (payload) {
            if (payload > max) {
                return max;
            }
            if (payload < min) {
                return min;
            }
            return '';
        };
        if (lockLimit) {
            if (onChange) {
                onChange(null, {
                    value: getEventPayloadValue(validValue),
                });
            }
            if (onInputChange) {
                onInputChange(null, {
                    value: getEventPayloadValue(validValue),
                });
            }
        }
    }, [getValidInputValue, lockLimit, max, min, onChange, onInputChange]);
    return (React.createElement("div", { className: cn(styles.component, (_b = {},
            _b[styles.block] = block,
            _b[styles.filled] = Boolean(value),
            _b[styles.hasLabel] = label,
            _b[styles.hasError] = Boolean(error),
            _b), styles[SIZE_TO_CLASSNAME_MAP[size]], className), "data-test-id": dataTestId },
        React.createElement(Input$1, __assign({}, restProps, customInputProps, { ref: ref, value: value.toString(), onChange: handleInputChange, onBlur: handleInputBlur, block: true, size: size, label: label, disabled: disabled, readOnly: readOnly, className: cn(inputClassName, styles.input), focusedClassName: cn(focusedClassName, styles.focused), fieldClassName: cn(fieldClassName, styles.field, (_c = {}, _c[styles.disabled] = disabled, _c), styles[SIZE_TO_CLASSNAME_MAP[size]]), inputMode: 'numeric', pattern: '[0-9]*', error: error, hint: hint, bottomAddons: !disabled && (React.createElement(Slider, { min: min, max: max, step: step, onStart: onSliderStart, onEnd: onSliderEnd, onChange: handleSliderChange, value: Number.isNaN(sliderValue) || !sliderValue ? min : sliderValue, disabled: disabled || readOnly, className: cn(styles.slider, styles[SIZE_TO_CLASSNAME_MAP[size]], (_d = {}, _d[styles.hidePips] = error || hint, _d), sliderClassName), pips: pips, range: range })), rightAddons: (info || rightAddons) && (React.createElement(Fragment, null,
                info && React.createElement("span", { className: styles.info }, info),
                rightAddons)) })),
        steps.length > 0 && !error && (React.createElement("div", { className: cn(styles.steps, stepsClassName) }, steps.map(function (stepLabel, i) {
            return isValidElement(stepLabel) ? (cloneElement(stepLabel, { key: i })) : (React.createElement("span", { key: i.toString() }, stepLabel));
        })))));
});
SliderInput.displayName = 'SliderInput';

export { SliderInput };
